import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
}

/**
 * Bez error boundary odmountuje React při nezachycené chybě v renderu celý strom
 * a uživatel vidí bílou stránku — na kterékoli stránce aplikace. Tohle z toho dělá
 * sdělitelnou chybu a nabídne obnovení.
 *
 * Fallback je záměrně bez vlastních komponent a bez routeru: běží v okamžiku, kdy
 * něco v aplikaci selhalo, takže má stát na co nejmenším množství kódu.
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
    return (
        <Sentry.ErrorBoundary
            fallback={({ eventId }) => (
                <div className="flex min-h-screen items-center justify-center p-6">
                    <div className="max-w-md text-center">
                        <h1 className="mb-4 text-3xl font-semibold text-ink-900">
                            Něco se pokazilo
                        </h1>
                        <p className="mb-8 text-ink-800">
                            Stránku se nepodařilo zobrazit. Zkuste ji prosím
                            obnovit. Pokud se to bude opakovat, napište nám a
                            přiložte kód níže.
                        </p>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="rounded-xl bg-product-500 px-6 py-3 font-semibold text-white transition-colors hover:bg-product-600"
                        >
                            Obnovit stránku
                        </button>
                        {eventId && (
                            <p className="mt-8 text-xs text-ink-700">
                                Kód chyby: {eventId}
                            </p>
                        )}
                    </div>
                </div>
            )}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
}
